<template>
  <div class="c-border-radius flex flex-col gap-2">
    <div v-if="isAdvancedMode" class="flex flex-col gap-2">
      <div class="flex gap-1">
        <div class="basis-1/2">
          <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.borderRadius.topLeft') }}</span>
          <NInputNumber
            v-model:value="values.topLeft"
            :format="(value) => `${value}px`"
            :min="0"
            :on-update:value="(value) => setBorderRadius(value, 'topLeft')"
            :parse="(formattedValue) => parseInt(formattedValue)"
            :step="2"
          />
        </div>
        <div class="basis-1/2">
          <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.borderRadius.topRight') }}</span>
          <NInputNumber
            v-model:value="values.topRight"
            :format="(value) => `${value}px`"
            :min="0"
            :on-update:value="(value) => setBorderRadius(value, 'topRight')"
            :parse="(formattedValue) => parseInt(formattedValue)"
            :step="2"
          />
        </div>
      </div>
      <div class="flex gap-1">
        <div class="basis-1/2">
          <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.borderRadius.bottomLeft') }}</span>
          <NInputNumber
            v-model:value="values.bottomLeft"
            :format="(value) => `${value}px`"
            :min="0"
            :on-update:value="(value) => setBorderRadius(value, 'bottomLeft')"
            :parse="(formattedValue) => parseInt(formattedValue)"
            :step="2"
          />
        </div>
        <div class="basis-1/2">
          <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.borderRadius.bottomRight') }}</span>
          <NInputNumber
            v-model:value="values.bottomRight"
            :format="(value) => `${value}px`"
            :min="0"
            :on-update:value="(value) => setBorderRadius(value, 'bottomRight')"
            :parse="(formattedValue) => parseInt(formattedValue)"
            :step="2"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <div class="basis-1/2">
        <NInputNumber
          v-model:value="values.topLeft"
          :format="(value) => `${value}px`"
          :min="0"
          :on-update:value="(value) => setBorderRadius(value)"
          :parse="(formattedValue) => parseInt(formattedValue)"
          :step="2"
        />
      </div>
    </div>
    <div class="text-size-xs">
      {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.borderRadius.switchTo') }}
      <span class="cursor-pointer underline color-primary" @click="switchMode">
        {{ isAdvancedMode ? 'simple mode' : 'advanced mode' }}
      </span>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { reactive, ref } from 'vue'
import { NInputNumber } from 'naive-ui'
import { useI18n } from 'vue-i18n'

export interface ThemeBorderRadius {
  topLeft: number
  topRight: number
  bottomLeft: number
  bottomRight: number
}

const emit = defineEmits(['update:value'])

const props = withDefaults(
  defineProps<{
    isAdvancedMode?: boolean
    values?: ThemeBorderRadius
  }>(),
  {
    isAdvancedMode: false,
    values: (props) => ({
      topLeft: props.values?.topLeft ?? 0,
      topRight: props.values?.topRight ?? 0,
      bottomLeft: props.values?.bottomLeft ?? 0,
      bottomRight: props.values?.bottomRight ?? 0,
    }),
  },
)

const { t } = useI18n()

const isAdvancedMode = ref(props.isAdvancedMode)
const values = reactive({
  topLeft: props.values.topLeft,
  topRight: props.values.topRight,
  bottomLeft: props.values.bottomLeft,
  bottomRight: props.values.bottomRight,
})

function setBorderRadius(value: number | null, position?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight') {
  if (value === null) return

  if (!isAdvancedMode.value) {
    values.topLeft = value
    values.topRight = value
    values.bottomLeft = value
    values.bottomRight = value
  } else if (isAdvancedMode.value && position) {
    values[position] = value
  }

  emit('update:value', values)
}

function switchMode() {
  isAdvancedMode.value = !isAdvancedMode.value

  values.topRight = values.topLeft
  values.bottomLeft = values.topLeft
  values.bottomRight = values.topLeft

  emit('update:value', values)
}
</script>
